import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { AppLayout } from '../components/Layout';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, ChevronRight, Zap, ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLUSTER_ACCENT = {
  agr: '#10b981', arc: '#f59e0b', art: '#a855f7', bus: '#3b82f6', edu: '#0ea5e9',
  fin: '#22c55e', gov: '#C6D4E8', hlt: '#ef4444', hos: '#f97316', hum: '#ec4899',
  it: '#06b6d4', law: '#6366f1', mfg: '#eab308', mkt: '#f43f5e', stem: '#8b5cf6', trn: '#14b8a6',
};

export default function NCCTEPathwaysPage() {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [clusters, setClusters] = useState([]);
  const [selected, setSelected] = useState(null);
  const [currentPathway, setCurrentPathway] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/nc/cte-pathways`, getAuthHeader()),
      axios.get(`${API}/nc/cdp`, getAuthHeader()),
    ]).then(([clRes, cdpRes]) => {
      setClusters(clRes.data.clusters || []);
      const pathway = cdpRes.data?.cte_pathway;
      if (pathway) {
        setCurrentPathway(pathway.id);
        setSelected(pathway.id);
      }
    }).catch(() => toast.error('Could not load pathways'))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await axios.post(`${API}/nc/cdp/section`, { section: 'cte_pathway_id', data: selected }, getAuthHeader());
      toast.success('CTE pathway saved');
      navigate('/app/nc-cdp');
    } catch {
      toast.error('Could not save pathway');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto space-y-6 pb-12" data-testid="nc-cte-page">
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <Link to="/app/nc-cdp" className="flex items-center gap-2 text-slate-300 hover:text-white text-sm mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Career Plan
          </Link>
          <h1 className="text-3xl font-bold text-white">NC CTE Career Pathways</h1>
          <p className="text-slate-300 text-sm mt-1">
            {currentPathway
              ? 'Your pathway is selected below. You can change it at any time.'
              : 'Choose the career cluster that aligns with your goals. Clusters scored by your assessment results.'}
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {clusters.map((cluster, i) => {
            const accent = CLUSTER_ACCENT[cluster.id] || '#94a3b8';
            const isSelected = selected === cluster.id;
            const isCurrent = currentPathway === cluster.id;
            return (
              <motion.button
                key={cluster.id}
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.03 }}
                onClick={() => setSelected(isSelected ? null : cluster.id)}
                className={`text-left rounded-xl border p-4 transition-all duration-200 ${
                  isSelected
                    ? 'border-opacity-60 bg-white/5'
                    : 'border-white/[0.06] bg-white/[0.02] hover:border-cyan-400/20'
                }`}
                style={isSelected ? { borderColor: accent + '60', boxShadow: `0 0 0 1px ${accent}40` } : {}}
                data-testid={`cte-cluster-${cluster.id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    {isCurrent && (
                      <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 mb-1 inline-block">
                        Current
                      </span>
                    )}
                    {cluster.fit_score > 0 && (
                      <div className="flex items-center gap-1 mb-1">
                        {[...Array(cluster.fit_score)].map((_, j) => (
                          <Zap key={j} className="w-3 h-3" style={{ color: accent }} />
                        ))}
                        <span className="text-[10px] text-slate-300">fit</span>
                      </div>
                    )}
                  </div>
                  {isSelected && <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: accent }} />}
                </div>

                <h3 className="text-white font-semibold text-sm leading-tight">{cluster.name}</h3>

                <p className="text-slate-300 text-xs mt-2 line-clamp-2">
                  {cluster.pathways?.slice(0, 3).join(' · ')}
                  {cluster.pathways?.length > 3 && ` +${cluster.pathways.length - 3} more`}
                </p>

                {cluster.nc_ic_examples?.length > 0 && (
                  <p className="text-[11px] text-amber-400/70 mt-2">
                    ★ {cluster.nc_ic_examples[0]}
                    {cluster.nc_ic_examples.length > 1 && ` +${cluster.nc_ic_examples.length - 1}`}
                  </p>
                )}
              </motion.button>
            );
          })}
        </div>

        {selected && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50"
          >
            <button
              onClick={save}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white font-semibold shadow-xl shadow-cyan-900/40 transition-colors disabled:opacity-60"
              data-testid="save-cte-pathway-btn"
            >
              {saving ? 'Saving...' : (currentPathway && selected !== currentPathway ? 'Update CTE Pathway' : (currentPathway ? 'Pathway Saved' : 'Save CTE Pathway'))}
              <ChevronRight className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </div>
    </AppLayout>
  );
}
